import React, { useState } from "react";
import Button from "react-bootstrap/Button";
import "./Patient.css";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FiActivity,
  FiArrowLeft,
  FiCalendar,
  FiDroplet,
  FiFlag,
  FiMail,
  FiPhone,
  FiShield,
  FiTarget,
  FiTrash2,
  FiUser,
} from "react-icons/fi";
import SymptomTrendsChart from "./SymptomTrendsChart";
import TreatmentComponent from "./TreatmentPlanPatient";
import AdheranceComponent from "./AdheranceList";
import NotesComponent from "./NotesList";
import ResponseSurveyList from "../SurveyManagment/ResponseSurveyList";
import Chat from "../../public/chat/Chat";
import DocumentsList from "./DocumentsList";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import { useSelector } from "react-redux";
import { deletePatientApi } from "../services/patientService";
import reddelete from "../../images/red-Delete.svg";
import PDFDownloader from "../ReportGeneration/DowunloadPdf";
import SymtomsList from "./SymtomsList";
import BladderPainMain from "../BladderPain/BladderPainMain";

function Patient() {
  const location = useLocation();
  const { patient, activeTabFromSate } = location.state || {};
  const [activeTab, setActiveTab] = useState(
    activeTabFromSate ? activeTabFromSate : "symptoms"
  );

  const navigate = useNavigate();
  const data = useSelector((state) => state?.login?.data);

  const deletePatient = async (id) => {
    try {
      const response = await deletePatientApi(id, { isDeleted: true });
      if (response?.status === "SUCCESS") {
        toast.success("Patient deleted successfully");
        navigate(-1);
      }
    } catch (error) {
    }
  };
  const colors = [
    "#66D4CF", // Mint
    // "#FFD60A", //  Yellow
    "#40C8E0", // Teal
    // "#BF5AF2", // Purple
    "#64D2FF", // Cyan
    "#E9E976", //   light yellow
    // "#5E5CE6", // Indigo
    "#e9c183", // light Orange
  ];

  const [modalShow, setModalShow] = React.useState(false);

  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    return Number.isNaN(date.getTime())
      ? "-"
      : date.toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
  };

  const tabs = [
    { id: "symptoms", label: "Symptom Trends" },
    { id: "symptoms-list", label: "Symptoms List" },
    { id: "treatment", label: "Treatment" },
    { id: "adherance", label: "Adherence" },
    { id: "bladder-journal", label: "Bladder Journal" },
    { id: "notes", label: "Notes", clinicalOnly: true },
    { id: "response", label: "Responses" },
    { id: "chat", label: "Chat", clinicalOnly: true },
    { id: "documents", label: "Documents" },
  ];

  const isClinicalUser = ["DOCTOR", "PROVIDER"].includes(data?.role);
  const visibleTabs = tabs.filter((tab) => !tab.clinicalOnly || isClinicalUser);
  const activeTabTitle =
    visibleTabs.find((tab) => tab.id === activeTab)?.label || "Patient activity";

  const handleTabKeyDown = (event, currentIndex) => {
    let nextIndex;
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % visibleTabs.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + visibleTabs.length) % visibleTabs.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = visibleTabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    const nextTab = visibleTabs[nextIndex];
    setActiveTab(nextTab.id);
    requestAnimationFrame(() => document.getElementById(`patient-tab-${nextTab.id}`)?.focus());
  };

  if (!patient) {
    return (
      <main className="patient-management">
        <section className="patient-missing-state" aria-labelledby="missing-patient-title">
          <span className="patient-missing-icon"><FiUser aria-hidden="true" /></span>
          <h1 id="missing-patient-title">Select a patient to view details</h1>
          <p>This page needs a patient selected from the dashboard or patient list.</p>
          <Button
            className="filled-btn"
            onClick={() => navigate(data?.role === "ADMIN" ? "/admin/dashboard" : "/doctor/dashboard")}
          >
            Go to patient listing
          </Button>
        </section>
      </main>
    );
  }

  const fullName = `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() || "Unnamed patient";
  const initials = `${patient?.firstName?.[0] || ""}${patient?.lastName?.[0] || ""}`.toUpperCase() || "P";
  const avatarColor = colors[
    [...fullName].reduce((total, character) => total + character.charCodeAt(0), 0) % colors.length
  ];
  const emergencyPhone = Array.isArray(patient?.emergencyContacts)
    ? patient.emergencyContacts[0]?.phone
    : patient?.emergencyContacts?.phone;

  return (
    <main className="patient-management">
        <header className="patient-page-header">
          <div className="patient-page-title">
            <button
              type="button"
              className="app-back-button"
              aria-label="Back to patient listing"
              onClick={() => navigate(data.role === "ADMIN" ? "/admin/dashboard" : "/doctor/dashboard")}
            >
              <FiArrowLeft aria-hidden="true" />
            </button>
            <div>
              <span className="patient-eyebrow">Patient record</span>
              <h1>Patient overview</h1>
            </div>
          </div>
          <div className="patient-page-actions">
            <PDFDownloader patientId={patient._id} />
            <button
              type="button"
              className="patient-delete-button"
              onClick={() => setModalShow(true)}
              aria-label={`Delete ${fullName}`}
            >
              <FiTrash2 aria-hidden="true" />
              <span>Delete</span>
            </button>
          </div>
        </header>

        <section className="patient-profile-card" aria-labelledby="patient-name">
          <div className="patient-profile-summary">
            <div className="patient-img">
              <div
                className="patient-initials"
                style={{ background: avatarColor }}
                aria-hidden="true"
              >
                {initials}
              </div>
            </div>
            <div className="patient-info-right">
              <div className="patient-name-row">
                <h2 id="patient-name" className="patient-name text-capitalize">{fullName}</h2>
                <span className="patient-status"><span aria-hidden="true" />Active patient</span>
              </div>
              <div className="patient-contact-list">
                <a href={patient?.email ? `mailto:${patient.email}` : undefined} aria-disabled={!patient?.email}>
                  <FiMail aria-hidden="true" />{patient?.email || "Email not available"}
                </a>
                <a href={patient?.phone ? `tel:${patient.phone}` : undefined} aria-disabled={!patient?.phone}>
                  <FiPhone aria-hidden="true" />{patient?.phone || "Phone not available"}
                </a>
              </div>
            </div>
          </div>

          <div className="patient-information-grid">
            <dl className="patient-info-item">
              <dt><FiUser aria-hidden="true" />Gender</dt>
              <dd>{patient?.gender || "-"}</dd>
            </dl>
            <dl className="patient-info-item">
              <dt><FiCalendar aria-hidden="true" />Date of birth</dt>
              <dd>{formatDate(patient?.birthDate)}</dd>
            </dl>
            <dl className="patient-info-item">
              <dt><FiFlag aria-hidden="true" />Nationality</dt>
              <dd>{patient?.nationality || "-"}</dd>
            </dl>
            <dl className="patient-info-item">
              <dt><FiDroplet aria-hidden="true" />Blood group</dt>
              <dd>{patient?.bloodGroup || "-"}</dd>
            </dl>
            <dl className="patient-info-item">
              <dt><FiActivity aria-hidden="true" />Diagnosed date</dt>
              <dd>{formatDate(patient?.diagnosedDate)}</dd>
            </dl>
            <dl className="patient-info-item patient-info-item-wide">
              <dt><FiTarget aria-hidden="true" />Care goals</dt>
              <dd className="patient-tag-list">
                {Array.isArray(patient?.goals) && patient.goals.length
                  ? patient.goals.map((goal) => <span key={goal}>{goal}</span>)
                  : "-"}
              </dd>
            </dl>
            <dl className="patient-info-item patient-info-item-wide">
              <dt><FiActivity aria-hidden="true" />Current symptoms</dt>
              <dd className="patient-tag-list patient-symptom-tags">
                {Array.isArray(patient?.symptoms) && patient.symptoms.length
                  ? patient.symptoms.map((symptom) => <span key={symptom}>{symptom}</span>)
                  : "-"}
              </dd>
            </dl>
            <dl className="patient-info-item patient-emergency-item">
              <dt><FiShield aria-hidden="true" />Emergency contact</dt>
              <dd>{emergencyPhone || patient?.phone || "-"}</dd>
            </dl>
          </div>
        </section>

        <section className="patient-workspace">
          <div className="patient-tabs-scroll" aria-label="Patient record sections">
            <div className="patient-tabs" role="tablist">
              {visibleTabs.map((tab, index) => (
                <button
                  key={tab.id}
                  id={`patient-tab-${tab.id}`}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls="patient-tab-panel"
                  tabIndex={activeTab === tab.id ? 0 : -1}
                  className={activeTab === tab.id ? "active" : ""}
                  onClick={() => setActiveTab(tab.id)}
                  onKeyDown={(event) => handleTabKeyDown(event, index)}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          <div
            id="patient-tab-panel"
            className="patient-tab-panel"
            role="tabpanel"
            aria-labelledby={`patient-tab-${activeTab}`}
          >
            <div className="patient-panel-heading">
              <span>Patient activity</span>
              <h2>{activeTabTitle}</h2>
            </div>
            <div className="chart-blk">
            {activeTab === "symptoms" && <SymptomTrendsChart data={patient} />}
            {activeTab === "symptoms-list" && (
              <SymtomsList data={patient?._id} />
            )}
            {activeTab === "treatment" && (
              <TreatmentComponent patient={patient._id} />
            )}
            {activeTab === "adherance" && (
              <AdheranceComponent patient={patient._id} />
            )}
            {activeTab === "bladder-journal" && (
              <BladderPainMain data={patient?._id} />
            )}
            {activeTab === "notes" && <NotesComponent patient={patient._id} />}
            {activeTab === "response" && (
              <ResponseSurveyList patient={patient._id} />
            )}
            {activeTab === "chat" && <Chat patient={patient} />}
            {activeTab === "documents" && (
              <DocumentsList patient={patient._id} />
            )}
            </div>
          </div>
        </section>

      {/* delete patient modal */}
      <Modal
        className="dlt-patient-modal"
        show={modalShow}
        size="lg"
        aria-labelledby="contained-modal-title-vcenter"
        centered
        onHide={() => setModalShow(false)}
      >
        <Modal.Body>
          <div className="d-flex justify-content-center mb-3">
            <div className="delete-img">
              <img src={reddelete} alt="img"></img>
            </div>
          </div>
          <h4>Are you sure? </h4>
          <p>This will permanently delete this patient record.</p>
          <div className="authentication-btns d-flex justify-content-center gap-4">
            <Button
              className="btn border-btn btn-no outlined-btn"
              onClick={() => setModalShow(false)}
            >
              No
            </Button>
            <Button
              className="btn dark-btn btn-yes"
              onClick={() => deletePatient(patient._id)}
            >
              Yes
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </main>
  );
}

export default Patient;
